<template>
    <section class="food-menu">
        <div class="inner">
            <BlocksCLFoodMenuSelection :menu="computedMenu" />
            <p v-if="allergens.length" class="allergens">
                <span v-for="allergen, key in allergens" :key="`allergen-${key}`">
                    <DSIcon :code="allergen.code" size="l" />
                    {{ $t(allergen.label) }}
                </span>
            </p>
            <p v-if="computedDescription" class="description">{{ computedDescription }}</p>
            <BlocksCLCards
                v-if="computedCards.length > 0"
                :key="`food-list-${selected.root}-${selected.category}`"
                class="product-cards"
                :count-override="display.count"
                :title="categoryTitle"
                :cards="computedCards"
                :card-background="display.transparentBackground === 'true' ? 'none' : 'white'"
                :food-menu="display.component === 'foodMenu'"
                tag="div"
            >
                <template v-for="{ specialties }, key in computedCards" :key="`specialties-${key}`" #[`byline-${key}`]>
                    <template v-for="{ code, label }, sKey in specialties" :key="`specialty-${sKey}`">
                        <DSIcon
                            class="allergen-icon"
                            size="xl"
                            :code="code"
                            :aria-label="$t(label)"
                        />
                    </template>
                </template>
                <template #external-link>
                    <nuxt-link
                        v-if="preorderLink"
                        class="preorder-link"
                        :to="preorderLink"
                    >
                        {{ $i18n.t('preorderLink') }}
                    </nuxt-link>
                </template>
            </BlocksCLCards>
        </div>
    </section>
</template>
<script setup>
import { ref, computed, storeToRefs, useNuxtApp, provide, useI18n, watch, useRouter, useRoute, onMounted } from '#imports';
import { usePageStore } from '~dsc/stores/cms/page';

const router = useRouter();
const route = useRoute();
const { station } = storeToRefs(usePageStore());
const { $paths, $i18n } = useNuxtApp();

const selected = ref({
    root: 0,
    category: 0,
});

provide('selected', selected);

defineOptions({
    name: 'CLFoodMenu',
});

const props = defineProps({
    contentType: {
        type: String,
        required: false,
        default: 'cardList',
    },
    internal: {
        type: String,
        required: false,
        default: '',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            component: 'foodMenu',
            count: 'many',
        }),
    },
    menu: {
        type: Array,
        required: false,
        default: () => [],
    },
    stations: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    cards: {
        type: Object,
        required: false,
        default: () => ({}),
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    isSchedulerEnabled: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const menuItems = computed(() => {
    if (station.value && station.value?.menu?.items) {
        return station.value.menu.items;
    } else if (props.menu?.[0]?.items) {
        return props.menu[0].items;
    }

    return [];
});

const allergens = computed(() => Array.from(new Set(computedCards.value.flatMap(item => item.specialties))).filter(v => v));

const computedMenu = computed(() => {
    if (menuItems.value.filter(isProductMenu).length > 0) {
        return menuItems.value.map((item, key) => ({
            id: item.id,
            index: key,
            title: item.title,
            description: item[key]?.description ?? station.value?.menu?.description,
            items: item.items?.filter(isProductMenu).map(({ id, title, description }, index) => ({
                id,
                index,
                title,
                description,
            })),
        })) ?? [];
    }

    return [];
});

const preorderLink = computed(() => {
    if (station.value?.stationTypes?.includes('helmisimpukkarestaurant')) {
        return `${$paths.getPath('preorder')}?station=${station.value?.slug}`;
    }

    return false;
});

const categoryTitle = computed(() => {
    if (menuItems.value?.filter(isProduct).length === menuItems.value?.length) {
        return props.menu?.[0]?.description ?? null;
    } else if (menuItems.value?.[selected.value.root]?.items?.length > 0) {
        return menuItems.value?.[selected.value.root]?.items?.[selected.value.category]?.title;
    } else if (menuItems.value?.[selected.value.root]?.title) {
        return menuItems.value?.[selected.value.root].title;
    }

    return '';
});

const computedDescription = computed(() => {
    if (menuItems.value?.filter(isProduct).length === menuItems.value?.length) {
        return menuItems.value.description;
    }

    const menuDescription = props.menu?.[0]?.description || station.value?.menu?.description;
    let description = menuDescription || $i18n.t('listPriceNotification');

    if (menuItems.value?.[selected.value.root]?.description) ({ description } = menuItems.value[selected.value.root]);

    const selectedCategory = menuItems.value?.[selected.value.root]?.items?.[selected.value.category];
    if (selectedCategory?.items) {
        return selectedCategory.description || description;
    }

    return description;
});

const computedCards = computed(() => {
    let currentCards = [];
    let categoryCards = [];
    if (menuItems.value?.filter(isProduct).length === menuItems.value?.length) {
        currentCards = menuItems.value;
    } else {
        const rootMenu = menuItems.value?.[selected.value.root];
        categoryCards = rootMenu?.items?.filter(isProduct);
        const selectedCategoryItems = rootMenu?.items?.[selected.value.category]?.items;
        if (selectedCategoryItems) {
            currentCards = selectedCategoryItems;
        } else if (categoryCards) {
            currentCards = categoryCards;
        }
    }

    if (!currentCards.length) {
        return [];
    }

    return currentCards.map(item => ({
        id: item.id,
        internal: item.internal,
        specialties: item.specialties?.map(specialty => allergensData.value.find(item => item.shorthand === specialty)),
        title: item.name,
        image: item.image,
        text: item.description,
        tags: item.tags,
        price: item.variations ? item.variations.map(priceVar => ({
            title: priceVar.name,
            price: priceVar.price,
        })) : [{ title: $i18n.t('price'), price: item.price }],
    }));
});

const allergensData = computed(() => [
    {
        shorthand: 'G',
        code: 'default-fill/gluten-free',
        label: 'allergens.glutenfree',
    },
    {
        shorthand: 'L',
        code: 'default-fill/lactose-free',
        label: 'allergens.lactosefree',
    },
    {
        shorthand: 'M',
        code: 'default-fill/milkless',
        label: 'allergens.milkless',
    },
    {
        shorthand: 'V',
        code: 'default-fill/vegan',
        label: 'allergens.vegan',
    },
    {
        shorthand: 'VL',
        code: 'default-fill/low-lactose',
        label: 'allergens.lowlactose',
    },
]);

function isProductMenu({ contentType }) {
    return [
        'productMenu',
        'helmisimpukkaProductMenu',
    ].includes(contentType);
}

function isProduct({ contentType }) {
    return [
        'product',
        'helmisimpukkaProduct',
    ].includes(contentType);
}

onMounted(() => {
    if (route.query?.menuId) {
        selected.value.root = props.menu?.[0]?.items?.findIndex(item => item.id === route.query?.menuId) || 0;
    }
});

watch(selected, to => {
    const selectedRoot = props.menu?.[0]?.items?.find((item, index) => index === to.root);

    if (selectedRoot) {
        router.replace({ query: { menuId: selectedRoot.id } });
    }
});

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            price: 'suositushinta',
            listPriceNotification: 'Verkossa näkyvät hinnat ovat suositushintoja. Varmista hinnat tilauksen yhteydessä asemalta.',
            allergens: {
                glutenfree: 'Gluteeniton',
                milkless: 'Maidoton',
                lactosefree: 'Laktoositon',
            },
        },
    },
});
</script>
<style scoped>
.food-menu.block {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.food-menu.block .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}
.food-menu .preorder-link {
  transition: all 0.2s ease-in-out;
  color: var(--color-primary-700);
  font-weight: 700;
}
.food-menu .preorder-link:hover {
  color: var(--color-primary-900);
}

.allergens span {
  display: inline-flex;
}
.allergens span:not(:last-child) {
  margin-right: 1rem;
}
.allergens .icon {
  margin-right: 0.25rem;
}

.description {
  font-weight: 700;
}

.allergen-icon:not(:last-child) {
  margin-right: 0.5rem;
}

.product-cards {
  padding-top: 1rem;
}
</style>
